import { createTheme } from '@mui/material';
import { fonts, colors, componentStyles } from '../styles/fontStyles';

const frostDarkTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      smMd700: 700, // custom breakpoint
      smMd800: 800, // custom breakpoint
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  custom: {
    modalBorderRadius: componentStyles.modalBorderRadius,
    modalBackgroundColor: componentStyles.modalBackgroundColor,
    modalTextColor: colors.white,
    dividerColor: colors.white,
    buttonTextColor: colors.white,
    buttonTextColorVariantOne: colors.green500,
    buttonBackgroundColor: colors.primaryBlue,
    buttonBackgroundHoverColor: colors.darkPrimaryBlue,
    report: {
      dataDecoration: {
        not_detected: {
          backgroundColor: colors.green700,
          fillFontColor: colors.blue900,
        },
        inconclusive: {
          backgroundColor: colors.gray300,
          fillFontColor: colors.blue900,
        },
        below_range: {
          backgroundColor: colors.green700,
          fillFontColor: colors.blue900,
        },
        detected: {
          backgroundColor: colors.primaryBlue,
          fillFontColor: colors.white,
        },
        above_range: {
          backgroundColor: colors.purple700,
          fillFontColor: colors.white,
        },
        unknown: {
          backgroundColor: colors.purple700,
          fillFontColor: colors.white,
        },
      },
      details: {
        header: {
          backgroundColor: colors.blue900,
          color: colors.white,
        },
        subHeader: {
          color: colors.green700,
        },
        resultCard: {
          slider: {
            previousCardBorderColor: colors.blue800,
            currentCardBorderColor: colors.primaryBlue,
            nextCardBorderColor: colors.blue800,
          },
          backgroundColor: colors.blue800,
          dateChip: {
            backgroundColor: colors.blue700,
            textColor: colors.white,
          },
          compoundsDetected: {
            countTextColor: colors.white,
            compoundDetectedTextColor: colors.softBlueLinks,
          },
          dividerLineColor: colors.m3SysDarkPrimary,
          totalPfas: {
            totalPfasScoreTextColor: colors.white,
            pfasUnitColor: colors.m3SysDarkPrimary,
            totalPfasTextColor: colors.softBlueLinks,
          },
        },
        reportNasemScoreBadge: {
          low: {
            exposureTextColor: colors.lightGreen,
            badgeTextColor: colors.lightGreen,
            badgeBackgroundColor: colors.forestGreen,
          },
          medium: {
            exposureTextColor: colors.lightBrown,
            badgeTextColor: colors.lightBrown,
            badgeBackgroundColor: colors.grayBrown,
          },
          high: {
            exposureTextColor: colors.lightRed,
            badgeTextColor: colors.lightRed,
            badgeBackgroundColor: colors.redBrown,
          },
        },
        resultTabNavigator: {
          button: {
            root: {
              color: colors.m3SysDarkPrimary,
              borderColor: colors.m3SysDarkOutline,
              backgroundColor: 'transparent',
            },
            selected: {
              color: colors.white,
              borderColor: colors.m3SysDarkOutline,
              backgroundColor: colors.primaryBlue,
              onHover: {
                backgroundColor: colors.primaryBlue,
              },
            },
          },
          tooltip: {
            color: colors.white,
            backgroundColor: colors.blue900,
            arrow: {
              color: colors.blue900,
            },
            boxShadow: '0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.30)',
          },
          images: {
            comparison: {
              root: {
                src: '/icons/chart_bar_sys_dark_primary.svg',
              },
              selected: {
                src: '/icons/chart_bar_white.svg',
              },
            },
            card: {
              root: {
                src: '/icons/cards_sys_dark_primary.svg',
              },
              selected: {
                src: '/icons/cards_white.svg',
              },
            },
            table: {
              root: {
                src: '/icons/list_sys_dark_primary.svg',
              },
              selected: {
                src: '/icons/list_white.svg',
              },
            },
            bubble: {
              root: {
                src: '/icons/bubble-view-sys-dark-primary.svg',
              },
              selected: {
                src: '/icons/bubble-view-white.svg',
              },
            },
            timeline: {
              root: {
                src: '/icons/timeline-sys-dark-primary.svg',
              },
              selected: {
                src: '/icons/timeline-white.svg',
              },
            },
          },
        },
        downloadPDFResult: {
          buttons: {
            ghost: {
              color: colors.green700,
            },
          },
          images: {
            download: {
              src: '/icons/download-green700.svg',
            },
          },
        },
        infoSection: {
          disclaimer: {
            title: {
              color: colors.greenTurquoise,
            },
            description: {
              color: colors.m3SysDarkPrimary,
            },
          },
          boxWrapper: {
            background: colors.blue900,
            fieldValue: {
              color: colors.white,
            },
            fieldName: {
              color: colors.m3SysDarkPrimary,
            },
          },
        },
        compoundCardsList: {
          cardContainer: {
            background: colors.midnightBlue,
            content: {
              compoundName: {
                color: colors.white,
              },
              unitDetails: {
                name: {
                  color: colors.m3SysDarkPrimary,
                },
                noValue: {
                  color: colors.m3SysDarkPrimary,
                },
                value: {
                  color: colors.m3SysDarkPrimary,
                },
              },
            },
          },
          indicator: {
            detected: {
              value: colors.primaryBlue,
            },
            not_detected: {
              value: '/icons/circle_green_outlined.svg',
            },
            below_range: {
              value: '/icons/circle_green_filled.svg',
            },
            above_range: {
              value: '/icons/arrow_purple_up.svg',
            },
            inconclusive: {
              value: '/icons/circle_gray_outlined.svg',
            },
          },
          compoundCardChipMeaning: {
            boxStyles: {
              color: colors.m3SysDarkPrimary,
            },
            chip: {
              default: {
                backgroundColor: colors.greenTurquoise,
                color: colors.white,
              },
              belowRange: {
                color: colors.blue900,
              },
              detected: {
                backgroundColor: colors.primaryBlue,
              },
              aboveRange: {
                backgroundColor: colors.purple700,
              },
            },
          },
          compoundDetailsResult: {
            backgroundColor: colors.blue800,
            renderBackToCompoundsListBtn: {
              default: {
                startIcon: '/icons/back-green-curved-arrow.svg',
                endIcon: '/icons/back-purple-curved-arrow.svg',
              },
              hover: {
                startIcon: '/icons/back-green-curved-arrow.svg',
                endIcon: '/icons/back-green-curved-arrow.svg',
              },
              desktopBackButtonColor: colors.m3SysDarkPrimary,
              mobileBackButtonColor: colors.greenTurquoise,
            },
            renderCompoundReportDetails: {
              testNameAbbrColor: colors.white,
              testNameOnlyColor: colors.white,
              boxStyles: {
                color: colors.m3SysDarkPrimary,
                h3color: colors.white,
                h4Color: colors.white,
                pColor: colors.m3SysDarkPrimary,
              },
            },
          },
          compoundDetailsPagination: {
            default: {
              mobileBackIcon: '/icons/back-green-arrow.svg',
              mobileNextIcon: '/icons/next-green-arrow.svg',
              desktopBackIcon: '/icons/back-white-arrow.svg',
              desktopNextIcon: '/icons/next-white-arrow.svg',
            },
            hover: {
              mobileBackIcon: '/icons/back-green-arrow.svg',
              mobileNextIcon: '/icons/next-green-arrow.svg',
              desktopBackIcon: '/icons/back-green-arrow.svg',
              desktopNextIcon: '/icons/next-green-arrow.svg',
            },
            mobileNextButtonColor: colors.green700,
            mobileBackButtonColor: colors.green700,
            desktopNextButtonColor: colors.white,
            desktopBackButtonColor: colors.white,
          },
          compoundDetailsChart: {
            renderCardWithLabelChip: {
              backgroundColor: colors.blue900,
              color: colors.white,
              borderColor: colors.blue900,
              detectionFrequencyColor: colors.m3SysDarkPrimary,
            },
            renderLevelValue: {
              nonDisplayValueColor: colors.white,
              displayValueColor: colors.white,
              levelColor: colors.m3SysDarkPrimary,
              unitColor: colors.m3SysDarkPrimary,
            },
            renderBarChart: {
              barChart: {
                xAxisFillColor: colors.white,
                yAxisFillColor: colors.slateGray,
                referenceLines: {
                  defaultStrokeColor: colors.white,
                  strokeColor: colors.slateGray,
                  labelFillColor: colors.slateGray,
                  y0: {
                    strokeColor: colors.white,
                    labelFillColor: colors.slateGray,
                  },
                },
                xData: {
                  fillColor: colors.green700,
                },
                yData: {
                  fillColor: colors.primaryBlue,
                },
                zData: {
                  specialLabelFillColor: colors.purple700,
                  fillColor: colors.primaryBlue,
                },
                badgeLabelConcentrationColor: colors.white,
              },
            },
          },
        },
        resultTable: {
          backgroundColor: colors.blue800,
          borderRadius: '16px',
          boxShadow:
            '0px 145px 41px 0px rgba(30, 30, 30, 0.00), 0px 93px 37px 0px rgba(30, 30, 30, 0.01), 0px 52px 31px 0px rgba(30, 30, 30, 0.05)',
          styledTableRow: {
            backgroundColor: colors.blue800,
            onHover: {
              backgroundColor: colors.blue800,
            },
          },
          styledHeaderCell: {
            color: colors.white,
            borderBottomColor: colors.m3SysDarkPrimary,
            backgroundColor: colors.midnightBlue,
          },
          styledLevelCell: {
            color: colors.m3SysDarkPrimary,
            borderBottomColor: colors.blue900,
          },
          styledRegularCell: {
            color: colors.m3SysDarkPrimary,
            borderBottomColor: colors.blue900,
          },
          paginationContainer: {
            backgroundColor: colors.blue900,
          },
          styledPagination: {
            paginationItemRoot: {
              backgroundColor: colors.blue900,
              color: colors.white,
            },
            onHover: {
              backgroundColor: colors.primaryBlue,
            },
            selected: {
              borderColor: colors.primaryBlue,
              color: colors.white,
              backgroundColor: colors.transparent,
            },
          },
          reportLabelTooltip: {
            tooltip: {
              backgroundColor: colors.blue900,
              color: colors.white,
              arrow: {
                color: colors.blue900,
              },
              boxShadowColor: '0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.30)',
            },
          },
          nameCell: {
            borderBottomColor: colors.blue900,
            shortName: {
              color: colors.white,
            },
            fullName: {
              color: colors.m3SysDarkPrimary,
            },
          },
          tableSort: {
            color: colors.white,
          },
          loadCompoundDetails: {
            backgroundColor: colors.grayDark,
          },
          drawer: {
            backgroundColor: colors.blue800,
            closeIcon: '/icons/close.svg',
          },
        },
      },
      timeline: {
        container: {
          backgroundColor: colors.blue800,
        },
        comparisonSelector: {
          backgroundColor: colors.blue800,
          typography: {
            compareResults: {
              color: colors.green700,
            },
            lowerThanYourResults: {
              color: colors.white,
            },
            populationBelowValue: {
              color: colors.white,
            },
            compoundFullname: {
              color: colors.white,
            },
            compoundNotInNhanes: {
              color: colors.green700,
            },
            selectCompound: {
              color: colors.m3SysDarkPrimary,
            },
          },
        },
        dropdownWrapper: {
          root: {
            color: colors.gray550,
          },
          select: {
            iconImageSrc: '/icons/chevron_down_gray.svg',
            notchedOutline: {
              default: { borderColor: colors.m3SysDarkPrimary },
              hover: { borderColor: colors.m3SysDarkPrimary },
              focused: {
                borderColor: colors.m3SysDarkPrimary,
                hover: {
                  borderColor: colors.m3SysDarkPrimary,
                },
              },
            },
            paper: {
              color: colors.m3SysDarkOnSurfaceVariant,
              backgroundColor: colors.blue800,
              borderColor: colors.m3SysDarkOutline,
              selectedOption: {
                backgroundColor: colors.blue700,
              },
            },
          },
        },
        filterByGender: {
          iconImageSrc: '/icons/check_small.svg',
          label: {
            color: colors.m3SysDarkPrimary,
          },
          buttonSelected: {
            backgroundColor: colors.m3SysDarkPrimary,
            borderColor: colors.m3SysDarkPrimary,
            color: colors.blue900,
          },
          buttonSelectedHover: {
            backgroundColor: colors.m3SysDarkPrimary,
            borderColor: colors.m3SysDarkPrimary,
            color: colors.blue900,
          },
          buttonNotSelected: {
            backgroundColor: 'transparent',
            borderColor: colors.m3SysDarkPrimary,
            color: colors.m3SysDarkPrimary,
          },
          buttonNotSelectedHover: {
            backgroundColor: 'transparent',
            borderColor: colors.m3SysDarkPrimary,
            color: colors.m3SysDarkPrimary,
          },
        },
        nasemScoreTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.m3SysDarkPrimary,
            },
            right: {
              color: colors.m3SysDarkPrimary,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.white,
              },
              axisLine: {
                color: colors.white,
              },
              tickLine: {
                color: colors.white,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
              y0: {
                stroke: {
                  color: colors.lightGray,
                },
                label: {
                  color: colors.gray600,
                },
              },
            },
            area: {
              low: {
                color: colors.forestGreen,
              },
              medium: {
                color: colors.grayBrown,
              },
              high: {
                color: colors.redBrown,
              },
            },
            line: {
              activeDot: {
                stroke: {
                  color: colors.black,
                },
              },
              dot: {
                stroke: {
                  color: colors.gray600,
                },
              },
            },
          },
          tooltip: {
            borderColor: colors.blue900,
            backgroundColor: colors.blue900,
            dateChip: {
              color: colors.white,
              backgroundColor: colors.blue700,
            },
            compoundsDetected: {
              detectedCountTextColor: colors.white,
              compoundDetectedTextColor: colors.m3SysDarkPrimary,
            },
            dividerLineColor: colors.m3SysDarkPrimary,
            totalPfas: {
              totalPfasScoreTextColor: colors.white,
              pfasUnitColor: colors.m3SysDarkPrimary,
              totalPfasTextColor: colors.softBlueLinks,
            },
          },
        },
        compoundInNHANESTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.m3SysDarkPrimary,
            },
            right: {
              color: colors.m3SysDarkPrimary,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.white,
              },
              axisLine: {
                color: colors.white,
              },
              tickLine: {
                color: colors.white,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
              y0: {
                stroke: {
                  color: colors.lightGray,
                },
                label: {
                  color: colors.gray600,
                },
              },
            },
            line: {
              activeDot: {
                default: {
                  circle: {
                    stroke: {
                      color: colors.white,
                    },
                  },
                },
              },
              dot: {
                isBeyondReportableRange: {
                  line: {
                    stroke: {
                      color: colors.primaryBlue,
                    },
                  },
                },
                isAboveRange: {
                  line: {
                    stroke: {
                      color: colors.purple700,
                    },
                  },
                },
                default: {
                  line: {
                    stroke: {
                      color: colors.primaryBlue,
                    },
                  },
                  circle: {
                    fill: {
                      color: colors.primaryBlue,
                    },
                    stroke: {
                      color: colors.primaryBlue,
                    },
                  },
                },
              },
            },
            images: {
              inconclusive: '/icons/circle_gray_outlined.svg',
              notDetected: '/icons/circle_green_outlined.svg',
              aboveRange: '/icons/arrow_purple_up.svg',
              belowRange: '/icons/circle_green_filled.svg',
              beyondReportableRange: '/icons/arrow_blue_up.svg',
            },
          },
          tooltip: {
            borderColor: colors.blue900,
            backgroundColor: colors.blue900,
            compoundDisplayValue: {
              color: colors.white,
            },
            compoundLevel: {
              color: colors.m3SysDarkPrimary,
            },
            dividerLineColor: colors.white,
            compoundDetectionFrequency: {
              color: colors.m3SysDarkPrimary,
            },
            compoundLoq: {
              color: colors.white,
            },
            compoundAboveRange: {
              color: colors.white,
            },
          },
        },
        compoundNotInNHANESTimelineChart: {
          customDoubleLabel: {
            left: {
              color: colors.m3SysDarkPrimary,
            },
            right: {
              color: colors.m3SysDarkPrimary,
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                color: colors.white,
              },
              axisLine: {
                color: colors.white,
              },
              tickLine: {
                color: colors.white,
              },
            },
            yAxis: {
              tick: {
                color: colors.gray600,
              },
              axisLine: {
                color: colors.gray600,
              },
              tickLine: {
                color: colors.gray600,
              },
            },
            referenceLines: {
              stroke: {
                color: colors.gray600,
              },
            },
            line: {
              activeDot: {
                default: {
                  circle: {
                    stroke: {
                      color: colors.white,
                    },
                  },
                },
              },
              dot: {
                isAboveRange: {
                  line: {
                    stroke: {
                      color: colors.purple700,
                    },
                  },
                },
                default: {
                  line: {
                    stroke: {
                      color: colors.primaryBlue,
                    },
                  },
                  circle: {
                    fill: {
                      color: colors.primaryBlue,
                    },
                    stroke: {
                      color: colors.primaryBlue,
                    },
                  },
                },
              },
            },
            images: {
              inconclusive: '/icons/circle_gray_outlined.svg',
              notDetected: '/icons/circle_green_outlined.svg',
              aboveRange: '/icons/arrow_purple_up.svg',
              belowRange: '/icons/circle_green_filled.svg',
              beyondReportableRange: '/icons/arrow_blue_up.svg',
            },
          },
        },
      },
      yourNasemScaleWithGuidelines: {
        backgroundColor: colors.blue900,
        nasemLevelColors: {
          low: colors.lightGreen,
          medium: colors.lightBrown,
          high: colors.lightRed,
        },
        renderNasemGuidelines: {
          guidelines: {
            default: {
              low: {
                backgroundColor: colors.forestGreen,
              },
              medium: {
                backgroundColor: colors.grayBrown,
              },
              high: {
                backgroundColor: colors.redBrown,
              },
            },
            selected: {
              low: {
                backgroundColor: colors.forestGreen,
              },
              medium: {
                backgroundColor: colors.grayBrown,
              },
              high: {
                backgroundColor: colors.redBrown,
              },
            },
          },
          yourCurrentPFASValueColor: colors.white,
        },
        renderYourNasemScale: {
          renderCollapseBtn: {
            backgroundColor: colors.blue800,
            collapseIcon: '/icons/chevron_down_green.svg',
          },
          nasemTextSubTitle: {
            color: colors.m3SysDarkPrimary,
          },
          nasemGuidance: {
            color: colors.white,
          },
        },
      },
      pfasAdviceGuidance: {
        box: {
          backgroundColor: colors.blue800,
        },
        pfasLowNasemLevelAdvice: {
          color: colors.white,
          understandYourTest: {
            defaultIcon: '/icons/description-green700.svg',
            hoverIcon: '/icons/description-green700.svg',
          },
        },
        pfasMediumNasemLevelAdvice: {
          renderScreeningAdvice: {
            color: colors.white,
            linkColor: colors.softBlueLinks,
          },
          renderHypertensiveAdvice: {
            color: colors.white,
            linkColor: colors.softBlueLinks,
          },
          renderBreastCancerAdvice: {
            color: colors.white,
            linkColor: colors.softBlueLinks,
          },
        },
        pfasHighNasemLevelAdvice: {
          color: colors.white,
          atAllWellVisitColor: colors.m3SysDarkPrimary,
          linkColor: colors.softBlueLinks,
        },
      },
      comparison: {
        container: {
          backgroundColor: colors.blue800,
        },
        renderCustomXAxisTick: {
          color: colors.white,
          specialTick: {
            twoColor: colors.lightGreen,
            twentyColor: colors.lightRed,
          },
        },
        composedChart: {
          gridColor: colors.gray600,
          xAxis: {
            axisLine: {
              stroke: colors.white,
            },
          },
          yAxis: {
            tick: {
              fill: colors.gray600,
            },
            axisLine: {
              stroke: colors.white,
            },
            tickLine: {
              stroke: colors.gray600,
            },
          },
          area: {
            low: {
              fill: colors.forestGreen,
            },
            medium: {
              fill: colors.grayBrown,
            },
            high: {
              fill: colors.redBrown,
            },
          },
          line: {
            stroke: colors.primaryBlue,
          },
        },
        reportNasemScoreBadgeAndText: {
          backgroundColor: colors.blue800,
          dividerLineColor: colors.m3SysDarkPrimary,
          yourNasemScoreColor: colors.white,
          exposureColor: {
            low: {
              color: colors.lightGreen,
            },
            medium: {
              color: colors.lightBrown,
            },
            high: {
              color: colors.lightRed,
            },
          },
        },
        barChart: {
          customDoubleLabel: {
            leftFill: colors.gray600,
            rightFill: colors.m3SysDarkPrimary,
          },
          referenceLines: {
            stroke: colors.gray600,
            y0: {
              stroke: colors.lightGray,
              label: {
                fill: colors.gray600,
              },
            },
          },
          bar: {
            low: {
              fill: colors.forestGreen,
            },
            medium: {
              fill: colors.grayBrown,
            },
            high: {
              fill: colors.redBrown,
            },
          },
        },
      },
      resultCTAOptions: {
        primaryBlueButton: {
          imageIcon: '/icons/download_white.svg',
        },
        understandYourTest: {
          defaultIcon: '/icons/description-green700.svg',
          hoverIcon: '/icons/description-green700.svg',
        },
        viewYourLatestResults: {
          defaultIcon: '/icons/description-green700.svg',
          hoverIcon: '/icons/description-green700.svg',
        },
      },
      howToUnderstandPfasCorePanelResult: {
        typography: {
          color: colors.white,
          h4: {
            color: colors.green700,
          },
        },
        renderExplainer: {
          color: colors.m3SysDarkPrimary,
        },
        renderTabsTitle: {
          root: {
            color: colors.m3SysDarkPrimary,
          },
          selected: {
            color: colors.green700,
          },
          backgroundColor: colors.blue900,
        },
      },
      bubbleViewContent: {
        backgroundColor: colors.blue800,
        typography: {
          body1: {
            color: colors.green700,
          },
          body2: {
            color: colors.white,
          },
        },
        bubbleChartChipMeaning: {
          chipStyles: {
            backgroundColor: colors.green700,
            color: colors.white,
          },
          box: {
            color: colors.m3SysDarkPrimary,
          },
          chip: {
            notDetected: {
              color: colors.blue900,
            },
            belowRange: {
              color: colors.blue900,
            },
            detected: {
              backgroundColor: colors.primaryBlue,
            },
            inconclusive: {
              borderColor: colors.gray300,
              backgroundColor: colors.gray300,
              color: colors.blue900,
            },
            aboveRange: {
              backgroundColor: colors.purple700,
            },
          },
        },
        compoundLevelsBubbleChart: {
          notDetectedIcon: '/icons/circle_green_outlined.svg',
          belowRangeIcon: '/icons/circle_green_filled.svg',
          aboveRangeIcon: '/icons/arrow_purple_up.svg',
          inconclusiveIcon: '/icons/circle_gray_outlined.svg',
          customScatterPoint: {
            detected: {
              circle: {
                fillColor: colors.blue600,
              },
            },
          },
          customTooltip: {
            backgroundColor: colors.blue900,
            color: colors.white,
            typography: {
              subtitle2: {
                color: colors.white,
              },
              body2: {
                color: colors.white,
              },
            },
          },
          composedChart: {
            xAxis: {
              tick: {
                strokeColor: colors.white,
              },
              axisLine: {
                strokeColor: colors.white,
              },
            },
            line: {
              strokeColor: colors.slateGray,
            },
          },
        },
      },
    },
    pages: {
      guest: {
        home: {
          backgroundColor: colors.blue900,
          typography: {
            color: colors.white,
          },
          featureCards: {
            backgroundColor: colors.blue800,
          },
          disclosure: {
            backgroundColor: colors.blue800,
            borderColor: colors.primaryBlue,
          },
          services: {
            dividerColor: 'rgba(208, 188, 255, 0.24)',
            card: {
              backgroundColor: colors.blue800,
              borderColor: 'rgba(92, 144, 247, 0.24)',
              boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.24)',
            },
            items: {
              hearingConservation: {
                accentColor: colors.frostOrange,
                surfaceColor: 'rgba(255, 115, 0, 0.18)',
              },
              occupationalServices: {
                accentColor: colors.blue300,
                surfaceColor: 'rgba(92, 144, 247, 0.18)',
              },
              respiratoryProtection: {
                accentColor: colors.respiratoryBlue,
                surfaceColor: 'rgba(0, 160, 207, 0.18)',
              },
              emergencyPersonnelServices: {
                accentColor: colors.lighterRed,
                surfaceColor: 'rgba(255, 107, 107, 0.16)',
              },
              hospitalServices: {
                accentColor: colors.lightGreen,
                surfaceColor: 'rgba(117, 229, 176, 0.16)',
              },
              agriculturalServices: {
                accentColor: colors.green300,
                surfaceColor: 'rgba(115, 227, 206, 0.16)',
              },
              schoolVisionHearingScreening: {
                accentColor: colors.blue300,
                surfaceColor: 'rgba(37, 99, 235, 0.22)',
              },
            },
          },
        },
      },
      footer: {
        copyright: {
          color: colors.m3SysDarkOutline,
        },
        termsOfService: {
          color: colors.m3SysDarkOutline,
        },
        privacyPolicy: {
          color: colors.m3SysDarkOutline,
        },
        support: {
          color: colors.primaryBlue,
          images: {
            src: '/icons/help-blue.svg',
          },
        },
      },
      userLogin: {
        backgroundColor: colors.blue900,
        typography: {
          h4: {
            color: colors.white,
          },
          subtitle1: {
            color: colors.white,
          },
          links: {
            color: colors.primaryBlue,
          },
        },
        textField: {
          label: {
            color: colors.m3SysDarkPrimary,
          },
        },
      },
      auth: {
        home: {
          backgroundColor: colors.blue900,
          typography: {
            color: colors.white,
            subHeader: {
              color: colors.green700,
            },
          },
          desktopMenu: {
            avatar: {
              backgroundColor: colors.blue800,
              borderColor: colors.blue300,
              color: colors.white,
            },
            notificationCount: {
              badge: {
                backgroundColor: colors.green700,
                borderColor: colors.white,
                color: colors.blue900,
              },
            },
          },
        },
      },
      howTo: {
        whatYourResultsMean: {
          backgroundColor: colors.blue900,
          renderSummaryStatistics: {
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
              body2: {
                color: colors.white,
              },
            },
          },
          summaryStatistics: {
            boxStyles: {
              backgroundColor: colors.blue800,
            },
            typography: {
              body1: {
                color: colors.green700,
              },
              body2: {
                color: colors.white,
              },
            },
          },
          renderAboutTestAndMedicalEffects: {
            boxStyles: {
              backgroundColor: colors.blue800,
            },
            typography: {
              body1: {
                color: colors.green700,
              },
              body2: {
                color: colors.white,
              },
              link: {
                color: colors.softBlueLinks,
              },
            },
          },
          renderResultTableDetails: {
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
              body2: {
                color: colors.white,
              },
              link: {
                color: colors.softBlueLinks,
              },
            },
          },
          renderSingleTableHeaderAndRow: {
            styledTableContainer: {
              backgroundColor: colors.blue800,
            },
            styledTableRow: {
              backgroundColor: colors.blue800,
              hover: {
                backgroundColor: colors.blue800,
              },
            },
            styledHeaderCell: {
              color: colors.white,
              borderBottom: colors.m3SysDarkPrimary,
              backgroundColor: colors.midnightBlue,
            },
            styledShortNameCell: {
              color: colors.white,
              borderBottom: colors.blue900,
            },
            styledRegularCell: {
              color: colors.m3SysDarkPrimary,
              borderBottom: colors.blue900,
            },
            headerSubtextStyle: {
              color: colors.white,
            },
            styledName: {
              color: colors.m3SysDarkPrimary,
            },
            nhanesIcon: '/icons/arrows-sort-white.svg',
            chip: {
              backgroundColor: colors.primaryBlue,
              color: colors.white,
            },
          },
        },
        howToUnderstandPFASStyles: {
          primaryBulletStyle: {
            color: colors.m3SysDarkPrimary,
          },
          primaryListItemStyle: {
            color: colors.m3SysDarkPrimary,
          },
          secondaryListItemStyle: {
            color: colors.white,
          },
          secondaryBulletStyle: {
            color: colors.white,
          },
        },
        howToReducePfas: {
          backgroundColor: colors.blue900,
          renderImmediateIdeasToTry: {
            immediateIdeas: {
              eatLessIcon: '/icons/pizza-off_sys_dark_primary.svg',
              noPlasticIcon: '/icons/soup_sys_dark_primary.svg',
              noSingleUseCup: '/icons/mug_sys_dark_primary.svg',
              getWaterTestIcon: '/icons/droplet_sys_dark_primary.svg',
              avoidNonStickIcon: '/icons/grill-spatula_sys_dark_primary.svg',
              avoidStrainResistantFabrics: '/icons/jacket_sys_dark_primary.svg',
              pfasFreeCosmeticsIcon: '/icons/perfume_sys_dark_primary.svg',
            },
            ideaCard: {
              color: colors.blue900,
              backgroundColor: colors.m3SysDarkPrimary,
            },
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
              body2: {
                color: colors.white,
              },
            },
          },
          renderWorkplacePFASExposure: {
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
              body2: {
                color: colors.white,
              },
            },
            renderWorkplacePFASInfo: {
              color: colors.m3SysDarkPrimary,
              boxStyles: {
                backgroundColor: colors.blue800,
              },
            },
            renderImportanceOfPFASMonitoring: {
              typography: {
                body1: {
                  color: colors.m3SysDarkPrimary,
                },
                body2: {
                  color: colors.white,
                },
              },
            },
          },
        },
        aboutTheTestMethodology: {
          backgroundColor: colors.blue900,
          renderUnderstandingTerminology: {
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
              body2: {
                color: colors.white,
              },
            },
            infoCard: {
              boxStyles: {
                backgroundColor: colors.blue800,
              },
              descriptionTextStyle: {
                color: colors.white,
              },
              headerTextStyle: {
                color: colors.white,
              },
            },
          },
          renderUsefulReferences: {
            typography: {
              body1: {
                color: colors.m3SysDarkPrimary,
              },
            },
            list: {
              color: colors.white,
            },
            styledLink: {
              color: colors.softBlueLinks,
            },
          },
        },
        takeSampleCollection: {
          boxContainer: {},
          tabs: {
            titleHeader: {
              selected: {
                color: colors.green700,
              },
              inactive: {
                color: colors.m3SysDarkPrimary,
              },
            },
            collectingSampleWarning: {
              color: colors.green700,
            },
          },
          allocateSomeMinutes: {
            backgroundColor: colors.blue800,
            color: colors.green700,
          },
        },
      },
      frost: {
        clientDownloadEmailTemplatePage: {
          typography: {
            h2: {
              color: colors.black,
            },
            h6: {
              color: colors.primaryBlue,
            },
          },
        },
        authHome: {
          typography: {
            body1: {
              color: colors.m3SysDarkPrimary,
            },
            body2: {
              color: colors.white,
            },
          },
          cards: {
            event: {
              backgroundColor: colors.blue800,
            },
            borderColor: colors.gray250,
            backgroundColor: colors.blue800,
          },
        },
      },
    },
    components: {
      toolTipFromDB: {
        images: {
          src: '/icons/tooltip-icon-sys-dark-primary.png',
        },
        tooltip: {
          backgroundColor: colors.blue900,
          color: colors.white,
          arrow: {
            color: colors.blue900,
          },
          boxShadowColor: '0px 8px 12px 6px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.30)',
        },
      },
      headerBanner: {
        background: colors.blue900,
        title: {
          color: colors.white,
        },
        subtitle: {
          color: colors.green700,
        },
        description: {
          color: colors.white,
        },
      },
      textWithPrimaryBlueBackground: {
        background: colors.primaryBlue,
        color: colors.white,
      },
      mainUserRegistration: {
        backgroundColor: colors.blue900,
      },
      registrationStepper: {
        backgroundColor: colors.blue900,
      },
      customStepper: {
        backgroundColor: colors.grayDark,
        progressBar: {
          backgroundColor: colors.green700,
        },
      },
      signupStep1AccountDetails: {
        typography: {
          color: colors.white,
          secondary: {
            color: colors.green700,
          },
        },
      },
      signupStep2ProfileInfo: {
        typography: {
          color: colors.white,
        },
      },
      signupStep3Confirmation: {
        typography: {
          color: colors.white,
        },
      },
      signupStep4Complete: {
        typography: {
          color: colors.white,
        },
      },
      textField: {
        typography: {
          color: colors.white,
        },
        color: colors.m3SysDarkPrimary,
        label: {
          color: colors.m3SysDarkPrimary,
        },
        muiOutlinedInputRoot: {
          color: colors.m3SysDarkOnSurfaceVariant,
        },
      },
      warningToMailSampleBack: {
        exclamationIcon: {
          src: '/icons/exclamation-green.svg',
        },
        title: {
          color: colors.green500,
        },
      },
      commonTestKitActivation: {
        over18Alert: {
          container: {
            backgroundColor: colors.blueDark,
            typography: {
              firstMessage: {
                color: colors.slateGray,
              },
              link: {
                color: colors.slateGray,
              },
            },
          },
        },
      },
      guestTestKitActivation: {
        containerStyles: {},
        headerStepper: {
          backOption: {
            color: colors.white,
          },
          image: {
            src: '/icons/exclamation-green.svg',
          },
          title: {
            color: colors.green500,
          },
        },
        steps: {
          successfulActivationOrRegistration: {
            explanationText: {
              color: colors.greenBlue,
            },
          },
        },
      },
      authTestKitActivation: {
        containerStyles: { padding: '17px 31px 0px 31px' },
      },
      customDialog: {
        content: {
          backgroundColor: colors.blue800,
          color: colors.white,
          title: {
            color: colors.lightBrown,
          },
          closeIcon: {
            src: '/icons/close.svg',
          },
        },
      },
      forgotPassword: {
        backgroundColor: colors.blue900,
        typography: {
          color: colors.white,
        },
      },
      resetPasswordConfirmation: {
        backgroundColor: colors.blue900,
        typography: {
          color: colors.white,
        },
      },
      customDatePicker: {
        popperContainer: {
          borderColor: colors.white,
          backgroundColor: colors.midnightBlue,
        },
        slotProps: {
          optionButtonStyles: {
            hover: {
              color: colors.white,
              backgroundColor: colors.blue300,
            },
            selected: {
              backgroundColor: colors.primaryBlue,
              color: colors.white,
              hover: {
                color: colors.white,
                backgroundColor: colors.blue300,
              },
              focus: {
                backgroundColor: colors.primaryBlue,
              },
            },
          },
        },
      },
    },
    profile: {
      editProfile: {
        editProfileButton: {
          color: colors.green500,
          imgSrc: '/icons/pencil_green.svg',
        },
      },
    },
    drawerMobileMenu: {
      background: colors.grayDark,
      listItemTextStyle: {
        color: colors.m3SysDarkPrimary,
      },
      notificationCountStyle: {
        color: colors.green700,
      },
      avatar: {
        fullName: {
          color: colors.white,
        },
        email: {
          color: colors.m3SysDarkOutline,
        },
      },
      icons: {
        profile: '/icons/user_blue.svg',
        messages: '/icons/messages_blue.svg',
        order: '/icons/shopping_cart_blue.svg',
        activate: '/icons/qrcode-blue.svg',
        results: '/icons/test_pipe_blue_small.svg',
        orders: '/icons/view-orders.svg',
        login: '/icons/user_blue.svg',
        signup: '/icons/register-hand-button.png',
        support: '/icons/help-blue.svg',
        privacyPolicy: '/icons/privacy-policy_blue.svg',
        termsOfService: '/icons/terms-of-service_blue.svg',
        logout: '/icons/logout_blue.svg',
      },
      iconButton: {
        color: colors.m3SysInverseSurface,
      },
    },
    messages: {
      container: {
        background: 'transparent',
      },
      modalStyle: {
        backgroundColor: colors.primaryBlue,
        color: colors.gray100,
      },
      showUnread: {
        checkBoxColor: colors.green700,
        checkboxCheckedColor: colors.green700,
        checkboxBorder: colors.green700,
        labelColor: colors.m3SysDarkOutline,
      },
      list: {
        backgroundColor: colors.blue900,
        labelColor: colors.white,
        subjectColor: colors.m3SysDarkPrimary,
        createdAtColor: colors.m3SysDarkOutline,
        borderUnread: 'transparent',
      },
      modal: {
        labelColor: colors.gray100,
        createdAtColor: colors.gray100,
        dividerColor: colors.gray100,
        descriptionColor: colors.gray100,
        closeButtonColor: colors.gray100,
        linkColor: colors.white,
      },
    },
    snackBarAlert: {
      severityTypeStyles: {
        success: {
          backgroundColor: colors.forestGreen,
          color: colors.white,
        },
        info: {
          backgroundColor: colors.primaryBlue,
          color: colors.white,
        },
        warning: {
          backgroundColor: colors.warningDark,
          color: colors.white,
        },
        error: {
          backgroundColor: colors.error800,
          color: colors.white,
        },
      },
    },
    orderPFAS: {
      container: {
        backgroundColor: colors.blue900,
      },
      stepper: {
        backgroundColor: colors.grayDark,
        backLinkColor: colors.white,
        progressBar: {
          backgroundColor: colors.green500,
        },
      },
      first: {
        shippingTextColor: colors.greenBlue,
      },
      subjects: {
        adultsOnlyMessage: {
          background: colors.blueDark,
          checkbox: {
            color: colors.green700,
            colorChecked: colors.green700,
          },
          color: colors.green700,
          colorTos: colors.m3SysDarkOutline,
          colorTosLink: colors.slateGray,
        },
      },
      subheader: {
        background: colors.primaryBlue,
        color: colors.white,
      },
      checkout: {
        redirectInstructionsColor: colors.primaryBlue,
        buttonInstructionsColor: colors.gray550,
      },
      thankyou: {
        color: colors.white,
      },
    },
  },
  support: {
    backgroundColor: colors.blue900,
    description: {
      color: colors.white,
    },
    categoryButton: {
      buttonSelected: {
        backgroundColor: colors.m3SysDarkPrimary,
        borderColor: colors.m3SysDarkPrimary,
        color: colors.blue900,
      },
      buttonSelectedHover: {
        backgroundColor: colors.m3SysDarkPrimary,
        borderColor: colors.m3SysDarkPrimary,
        color: colors.blue900,
      },
      buttonNotSelected: {
        backgroundColor: 'transparent',
        borderColor: colors.m3SysDarkPrimary,
        color: colors.m3SysDarkPrimary,
      },
      buttonNotSelectedHover: {
        backgroundColor: 'rgba(208, 188, 255, 0.1)',
        borderColor: colors.m3SysDarkPrimary,
        color: colors.m3SysDarkPrimary,
      },
    },
    articleDetails: {
      background: colors.blue800,
      backgroundExpanded: colors.blue800,
      title: {
        expandedColor: colors.greenTurquoise,
        notExpandedColor: colors.m3SysDarkPrimary,
      },
      body: {
        links: colors.softBlueLinks,
        text: colors.white,
      },
    },
    contactForm: {
      placeholder: {
        color: colors.m3SysDarkOnSurfaceVariant,
      },
      label: {
        color: colors.white,
        backgroundColor: colors.blue900,
      },
      border: {
        color: colors.m3SysDarkPrimary,
      },
    },
  },
  profile: {
    backgroundColor: colors.blue900,
    leftMenu: {
      button: {
        color: colors.m3SysDarkOnSurfaceVariant,
        inactive: {
          backgroundColor: colors.blue900,
          borderRadius: '0px',
        },
        active: {
          backgroundColor: colors.blue800,
          borderRadius: '100px',
        },
        hover: {
          backgroundColor: colors.blue800,
        },
      },
      profile: {
        activeIcon: '/icons/user_blue.svg',
        inactiveIcon: '/icons/user_blue.svg',
      },
      messages: {
        activeIcon: '/icons/messages_blue.svg',
        inactiveIcon: '/icons/messages_blue.svg',
        notificationCount: {
          backgroundColor: colors.green700,
          border: '1px solid #FFF',
          color: colors.blue900,
        },
      },
      appearance: {
        activeColor: colors.primaryBlue,
        inactiveColor: colors.primaryBlue,
      },
      privacyPolicy: {
        activeIcon: '/icons/privacy-policy_blue.svg',
        inactiveIcon: '/icons/privacy-policy_blue.svg',
      },
      termsOfService: {
        activeIcon: '/icons/terms-of-service_blue.svg',
        inactiveIcon: '/icons/terms-of-service_blue.svg',
      },
      logout: {
        activeIcon: '/icons/logout_blue.svg',
        inactiveIcon: '/icons/logout_blue.svg',
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primaryBlue,
    },
    background: {
      default: colors.blue900,
    },
  },
  buttons: {
    primaryBlue: {
      borderColor: colors.primaryBlue,
      background: colors.primaryBlue,
      hover: {
        background: colors.blue700,
        borderColor: colors.blue700,
      },
      disabled: {
        background: colors.blue800,
        borderColor: 'transparent',
        color: colors.blue300,
      },
    },
    ghost: {
      borderColor: colors.green700,
      color: colors.green700,
      hover: {
        color: colors.green700,
        borderColor: colors.green700,
      },
    },
  },
  typography: {
    fontFamily: fonts.regular,
    h1: {
      fontFamily: fonts.titlesNavButtons,
    },
    h2: {
      fontFamily: fonts.titlesNavButtons,
    },
    h3: {
      fontFamily: fonts.titlesNavButtons,
    },
    h4: {
      fontFamily: fonts.titlesNavButtons,
    },
    h5: {
      fontFamily: fonts.titlesNavButtons,
    },
    h6: {
      fontFamily: fonts.titlesNavButtons,
    },
    links: {
      default: {
        color: colors.green700,
        hoverColor: colors.green700,
      },
      desktopMenu: {
        color: colors.white,
        activeColor: colors.green500,
        underlineNavigationActive: colors.green700,
      },
    },
  },
  images: {
    logo: {
      src: '/frost/logo_blue.png',
    },
    activateKitIcon: {
      default: '/icons/qr-code-green-light.svg',
      hover: '/icons/qr-code-green-light.svg',
    },
    viewResultsIcon: {
      default: '/icons/description-green700.svg',
      hover: '/icons/description-green700.svg',
    },
    navigateBackIcon: {
      src: '/icons/back-white-arrow.svg',
    },
    navigateNextIcon: {
      src: '/icons/next-white-arrow.svg',
    },
    desktopMenu: {
      results: {
        default: '/icons/menu_pipe_blue.svg',
        active: '/icons/pipe_menu_green_turquoise.svg',
      },
      orders: {
        default: '/icons/shopping_cart_dark_inactive.svg',
        active: '/icons/shopping_cart_dark_active.svg',
      },
      support: {
        default: '/icons/help-blue.svg',
        active: '/icons/support-help_green.svg',
      },
      messages: {
        default: '/icons/messages_gray550.svg',
        active: '/icons/messages_blue.svg',
      },
      calendar: {
        default: '/icons/calendar_gray550.svg',
        active: '/icons/calendar_gray550.svg',
      },
      records: {
        default: '/icons/medical_services_gray550.svg',
        active: '/icons/medical_services_gray550.svg',
      },
    },
    createAccount: {
      src: '/icons/register-hand-button.png',
    },
    editIcon: {
      default: '/icons/pencil_green.svg',
      hover: '/icons/pencil_green.svg',
    },
    deleteIcon: {
      default: '/icons/trash_can_green700.svg',
      hover: '/icons/trash_can_green700.svg',
    },
    closeIcon: {
      default: '/icons/close.svg',
      hover: '/icons/close.svg',
    },
    calendarIcon: {
      default: '/icons/calendar.svg',
      hover: '/icons/calendar.svg',
    },
    locationIcon: {
      default: '/icons/location.svg',
      hover: '/icons/location.svg',
    },
    peopleIcon: {
      default: '/icons/people.svg',
      hover: '/icons/people.svg',
    },
    requirementIcons: {
      lungs: {
        default: '/icons/lungs_default.svg',
        highlighted: '/icons/lungs_highlighted.svg',
      },
    },
    poweredByRH: {
      src: '/images/powered-by-RH-white-and-blue.png',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: fonts.titlesNavButtons,
          textTransform: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: fonts.titlesNavButtons,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: componentStyles.modalBackgroundColor,
          borderRadius: 20,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: colors.midnightBlue,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: colors.m3SysDarkPrimary,
          '&.Mui-focused': {
            color: colors.m3SysDarkPrimary,
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: colors.m3SysDarkOnSurfaceVariant,
        },
      },
    },
    MuiTextField: {
      color: colors.m3SysDarkPrimary,
      label: {
        color: colors.m3SysDarkPrimary,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          marginBottom: 2,
          '& .MuiInputLabel-asterisk': {
            display: 'none',
          },
          // only show the asterisk if the label is shrinked(focused)
          '& .MuiInputLabel-root.Mui-required[data-shrink="true"]::after': {
            content: '" *"',
            color: colors.white,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            '& fieldset': {
              borderColor: componentStyles.textFieldBorderColor,
            },
            '&:hover fieldset': {
              borderColor: componentStyles.textFieldBorderColor,
            },
          },

          '& .MuiInputBase-root': {
            borderColor: componentStyles.textFieldBorderColor,
            color: componentStyles.textFieldInputColor,
            fontFamily: fonts.regular,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: 0.5,
            '& fieldset': {
              borderColor: componentStyles.textFieldBorderColor,
            },
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              color: colors.m3SysDarkOnSurfaceVariant,
              borderColor: colors.m3SysDarkPrimary,
            },
            '&:hover fieldset': {
              color: colors.m3SysDarkOnSurfaceVariant,
              borderColor: colors.m3SysDarkPrimary,
            },
            '&.Mui-focused fieldset': {
              color: colors.m3SysDarkOnSurfaceVariant,
              borderColor: colors.m3SysDarkPrimary,
            },
            '& input': {
              color: colors.m3SysDarkOnSurfaceVariant,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.m3SysDarkOnSurfaceVariant,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // Customization for the Select Dropdown elements
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: componentStyles.textFieldBorderColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: componentStyles.textFieldBorderColor,
          },
          '&:active .MuiOutlinedInput-notchedOutline': {
            borderColor: componentStyles.textFieldBorderColor,
          },
        },
        notchedOutline: {
          borderColor: componentStyles.textFieldBorderColor,
          borderWidth: '2px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: fonts.regular,
          fontSize: '1rem',
        },
        message: {
          fontFamily: fonts.regular,
          fontSize: '1rem',
        },
      },
    },
  },
  layout: {
    defaults: {
      backgroundColor: colors.blue900,
    },
  },
});

export default frostDarkTheme;
